<template>
  <CCard class="staff-ui-card bg-white">
    <CCardHeader class="staff-ui-header d-flex justify-content-between align-items-center">
      <h4 class="card-title mb-0 text-white d-flex align-items-center">
        <CIcon name="cil-briefcase" class="mr-2 text-white" />
        {{ $t("jobTypeMgmt.pageTitle") }}
      </h4>
      <CButton class="staff-ui-header-button staff-ui-header-button--add" @click="openAddModal">
        <CIcon name="cil-plus" />
        &nbsp;{{ $t("jobTypeMgmt.addJobType") }}
      </CButton>
    </CCardHeader>

    <CCardBody class="staff-ui-body">
      <div v-if="myDepartment">
        <!-- Summary Bar -->
        <div class="staff-ui-summary">
          <div>
            <span>{{ $t("staffMgmt.deptLabel") }}</span>
            <strong>{{ myDepartment.departmentName }}</strong>
          </div>
          <div class="staff-ui-count">
            {{ (myDepartment.subCategories || []).length }} {{ $t("jobList.jobsUnit") || 'ประเภทงาน' }}
          </div>
        </div>

        <!-- Mobile List View (Cards) -->
        <div class="management-mobile-list">
          <div class="staff-ui-mobile-search" v-if="(myDepartment.subCategories || []).length > 3">
            <CIcon name="cil-search" />
            <input
              v-model.trim="mobileSearch"
              type="search"
              :placeholder="$t('staffMgmt.searchPlaceholder') || 'ค้นหาชื่อประเภทงาน...'"
              @input="mobileActivePage = 1"
            />
          </div>

          <article
            v-for="item in mobilePageSubCategories"
            :key="item._id || item.name"
            class="management-mobile-card"
          >
            <div class="management-mobile-card__head">
              <CIcon :name="item.icon || 'cil-briefcase'" />
              <div>
                <small>{{ $t("jobTypeMgmt.jobTypeName") }}</small>
                <strong>{{ item.name }}</strong>
              </div>
            </div>
            <div class="management-mobile-card__content pb-2">
              <div class="d-flex align-items-center justify-content-between">
                <span class="text-muted font-weight-bold">{{ $t("jobTypeMgmt.slaHours") }}:</span>
                <span class="badge-sla-tag">
                  ⏱️ {{ item.slaHours || 1 }} {{ $t("jobList.hoursUnit") || 'ชม.' }}
                </span>
              </div>
            </div>
            <div class="management-mobile-actions">
              <button
                type="button"
                class="staff-ui-action staff-ui-action--edit"
                @click="openEditModal(item)"
              >
                <CIcon name="cil-pencil" />
                <span>{{ $t('mgmt.edit') }}</span>
              </button>
              <button
                type="button"
                class="staff-ui-action staff-ui-action--delete"
                @click="deleteSubCategory(item)"
              >
                <CIcon name="cil-trash" />
                <span>{{ $t('mgmt.delete') }}</span>
              </button>
            </div>
          </article>

          <div v-if="!mobilePageSubCategories.length" class="staff-ui-mobile-empty">
            {{ $t('staffMgmt.noResults') || 'ไม่พบข้อมูล' }}
          </div>

          <div v-if="mobileTotalPages > 1" class="staff-ui-mobile-pagination">
            <button :disabled="mobileActivePage <= 1" @click="mobileActivePage--">‹</button>
            <span>{{ mobileActivePage }} / {{ mobileTotalPages }}</span>
            <button :disabled="mobileActivePage >= mobileTotalPages" @click="mobileActivePage++">›</button>
          </div>
        </div>

        <!-- Desktop Table View -->
        <div class="responsive-management-table">
          <CDataTable
            :items="myDepartment.subCategories"
            :fields="subCategoryFields"
            hover
            striped
            :items-per-page="100"
            :no-footer="true"
          >
            <template #name="{ item }">
              <td class="align-middle font-weight-bold">
                <CIcon :name="item.icon || 'cil-briefcase'" class="job-type-icon mr-2" />
                {{ item.name }}
              </td>
            </template>

            <template #department="{ item }">
              <td class="align-middle">
                <span v-if="getDepartmentName(item)" class="badge badge-light border">
                  {{ getDepartmentName(item) }}
                </span>
                <span v-else class="text-muted small">- ไม่ระบุฝ่าย -</span>
              </td>
            </template>

            <template #slaHours="{ item }">
              <td class="text-center align-middle">
                <span class="badge-sla-tag">
                  ⏱️ {{ item.slaHours || 1 }} {{ $t("jobList.hoursUnit") || 'ชม.' }}
                </span>
              </td>
            </template>

            <template #actions="{ item }">
              <td class="text-center align-middle actions-column">
                <div class="d-flex justify-content-center gap-2 flex-nowrap">
                  <CButton
                    size="sm"
                    class="staff-ui-action staff-ui-action--edit"
                    @click="openEditModal(item)"
                  >
                    <CIcon name="cil-pencil" />
                    <span>{{ $t('mgmt.edit') }}</span>
                  </CButton>
                  <CButton
                    size="sm"
                    class="staff-ui-action staff-ui-action--delete ml-2"
                    @click="deleteSubCategory(item)"
                  >
                    <CIcon name="cil-trash" />
                    <span>{{ $t('mgmt.delete') }}</span>
                  </CButton>
                </div>
              </td>
            </template>
          </CDataTable>
        </div>
      </div>

      <div v-else class="text-center py-5">
        <div class="spinner-border text-primary" role="status"></div>
        <div class="mt-2">{{ $t("mgmt.loadingDept") }}</div>
      </div>

      <!-- Add Modal -->
      <CModal
        :title="$t('jobTypeMgmt.addSubModal')"
        size="md"
        :show.sync="addModal"
        class="management-add-modal"
        centered
      >
        <div class="p-2">
          <CInput
            :label="$t('jobTypeMgmt.jobTypeName')"
            :placeholder="$t('jobTypeMgmt.subNamePh')"
            v-model="newSubName"
          />
          <CInput
            :label="$t('jobTypeMgmt.slaHours')"
            type="number"
            min="1"
            :placeholder="$t('jobTypeMgmt.slaPh')"
            v-model.number="newSlaHours"
          />
          <div class="form-group">
            <label>ไอคอนประเภทงาน</label>
            <div class="job-icon-select">
              <CIcon :name="newSubIcon" />
              <select v-model="newSubIcon" class="form-control">
                <option v-for="option in iconOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
              </select>
            </div>
          </div>
        </div>

        <template #footer>
          <CButton class="management-modal-cancel" @click="addModal = false">
            {{ $t("mgmt.cancel") }}
          </CButton>
          <CButton class="management-modal-save" @click="addSubCategory">
            {{ $t("mgmt.add") }}
          </CButton>
        </template>
      </CModal>

      <!-- Edit Modal -->
      <CModal
        :title="$t('jobTypeMgmt.editSubModal')"
        size="md"
        :show.sync="editModal"
        class="management-edit-modal"
        centered
      >
        <CModalBody v-if="editSubCategory" class="p-2">
          <CForm>
            <div>
              <CInput
                :label="$t('jobTypeMgmt.jobTypeName')"
                :placeholder="$t('jobTypeMgmt.subNamePh')"
                v-model="editSubCategory.name"
              />
              <CInput
                :label="$t('jobTypeMgmt.slaHours')"
                type="number"
                min="1"
                :placeholder="$t('jobTypeMgmt.slaPh')"
                v-model.number="editSubCategory.slaHours"
              />
              <div class="form-group">
                <label>ไอคอนประเภทงาน</label>
                <div class="job-icon-select">
                  <CIcon :name="editSubCategory.icon || 'cil-briefcase'" />
                  <select v-model="editSubCategory.icon" class="form-control">
                    <option v-for="option in iconOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                  </select>
                </div>
              </div>
            </div>
          </CForm>
        </CModalBody>
        <template #footer>
          <CButton class="management-modal-cancel" @click="editModal = false">
            {{ $t("mgmt.cancel") }}
          </CButton>
          <CButton class="management-modal-save" @click="saveChanges">
            {{ $t("jobTypeMgmt.saveEditBtn") }}
          </CButton>
        </template>
      </CModal>
    </CCardBody>
  </CCard>
</template>

<script>
import CModal from '@/components/common/AccessibleModal.vue';
import api from "@/service/api";
import Swal from "sweetalert2";

export default {
  components: { CModal },
  name: "JobTypeManagementSupervisor",
  data() {
    return {
      myDepartment: null,

      editModal: false,
      addModal: false,

      newSubName: "",
      newSlaHours: 1,
      newSubIcon: "cil-briefcase",
      editSubCategory: null,

      iconOptions: [
        { value: "cil-briefcase", label: "งานทั่วไป" },
        { value: "cil-settings", label: "เครื่องจักร/ระบบ" },
        { value: "cil-bolt", label: "ไฟฟ้า" },
        { value: "cil-drop", label: "ประปา" },
        { value: "cil-building", label: "อาคาร" },
        { value: "cil-home", label: "สถานที่" },
        { value: "cil-lightbulb", label: "แสงสว่าง" },
        { value: "cil-monitor", label: "คอมพิวเตอร์/จอภาพ" },
        { value: "cil-phone", label: "โทรศัพท์" },
        { value: "cil-print", label: "เครื่องพิมพ์" },
        { value: "cil-truck", label: "ยานพาหนะ" },
        { value: "cil-warning", label: "งานเร่งด่วน" },
      ],

      mobileSearch: "",
      mobileActivePage: 1,
      mobilePerPage: 10,
    };
  },

  mounted() {
    this.fetchMyDepartment();
  },

  computed: {
    subCategoryFields() {
      return [
        {
          key: "name",
          label: this.$t("jobTypeMgmt.jobTypeName"),
          _style: "width: 55%",
        },
        {
          key: "slaHours",
          label: this.$t("jobTypeMgmt.slaHours"),
          _style: "width: 20%; text-align:center;",
          class: "text-center",
        },
        {
          key: "actions",
          label: "",
          _style: "width: 25%",
          class: "text-center",
        },
      ];
    },

    filteredSubCategories() {
      const subs = this.myDepartment?.subCategories || [];
      if (!this.mobileSearch) return subs;
      const kw = this.mobileSearch.toLowerCase().trim();
      return subs.filter(
        (sub) =>
          (sub.name || "").toLowerCase().includes(kw) ||
          String(sub.slaHours || "").includes(kw)
      );
    },

    mobileTotalPages() {
      return Math.ceil(this.filteredSubCategories.length / this.mobilePerPage) || 1;
    },

    mobilePageSubCategories() {
      const start = (this.mobileActivePage - 1) * this.mobilePerPage;
      return this.filteredSubCategories.slice(start, start + this.mobilePerPage);
    },
  },

  methods: {
    async fetchMyDepartment() {
      try {
        const token = localStorage.getItem("accessToken");
        const res = await axios.get(
          `${process.env.VUE_APP_APP_URL}/category/supervisor`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        this.myDepartment = res.data.data;
      } catch (err) {
        console.error(err);
        Swal.fire(
          this.$t("mgmt.error"),
          this.$t("jobTypeMgmt.cannotLoadDept"),
          "error"
        );
      }
    },

    openAddModal() {
      this.newSubName = "";
      this.newSlaHours = 1;
      this.newSubIcon = "cil-briefcase";
      this.addModal = true;
    },

    openEditModal(item) {
      this.editSubCategory = JSON.parse(JSON.stringify(item));
      this.editSubCategory.icon = this.editSubCategory.icon || "cil-briefcase";
      this.editModal = true;
    },

    async addSubCategory() {
      if (!this.newSubName.trim() || !this.newSlaHours) {
        Swal.fire(
          this.$t("mgmt.warn"),
          this.$t("jobTypeMgmt.warnComplete"),
          "warning"
        );
        return;
      }

      try {
        const updatedCategory = JSON.parse(JSON.stringify(this.myDepartment));

        updatedCategory.subCategories.push({
          name: this.newSubName.trim(),
          slaHours: parseInt(this.newSlaHours, 10),
          icon: this.newSubIcon,
        });

        await axios.put(
          `${process.env.VUE_APP_APP_URL}/category`,
          updatedCategory
        );

        Swal.fire(
          this.$t("mgmt.success"),
          this.$t("jobTypeMgmt.addSubOk"),
          "success"
        );
        this.addModal = false;
        this.fetchMyDepartment();
      } catch (err) {
        Swal.fire(
          this.$t("mgmt.error"),
          err.response?.data?.message || this.$t("mgmt.addFail"),
          "error"
        );
      }
    },

    async saveChanges() {
      if (!this.editSubCategory.name.trim() || !this.editSubCategory.slaHours) {
        Swal.fire(
          this.$t("mgmt.warn"),
          this.$t("jobTypeMgmt.warnComplete"),
          "warning"
        );
        return;
      }

      try {
        const updatedCategory = JSON.parse(JSON.stringify(this.myDepartment));

        const index = updatedCategory.subCategories.findIndex(
          (sub) => sub._id === this.editSubCategory._id
        );

        if (index !== -1) {
          updatedCategory.subCategories[index] = {
            ...this.editSubCategory,
            name: this.editSubCategory.name.trim(),
            slaHours: parseInt(this.editSubCategory.slaHours, 10),
          };
        }

        await axios.put(
          `${process.env.VUE_APP_APP_URL}/category`,
          updatedCategory
        );

        Swal.fire(
          this.$t("mgmt.success"),
          this.$t("mgmt.saveSaved"),
          "success"
        );
        this.editModal = false;
        this.fetchMyDepartment();
      } catch (err) {
        Swal.fire(
          this.$t("mgmt.error"),
          err.response?.data?.message || this.$t("mgmt.editFail"),
          "error"
        );
      }
    },

    async deleteSubCategory(item) {
      Swal.fire({
        title: this.$t("jobTypeMgmt.delSubTitle"),
        text: this.$t("jobTypeMgmt.delSubLine").replace("{name}", item.name),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("mgmt.delete"),
        cancelButtonText: this.$t("mgmt.cancel"),
      }).then(async (result) => {
        if (result.isConfirmed) {
          Swal.fire({
            title: this.$t("mgmt.sureDelete"),
            text: this.$t("jobTypeMgmt.subWillDeleteLine").replace(
              "{name}",
              item.name
            ),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: this.$t("mgmt.confirmDelete"),
            cancelButtonText: this.$t("mgmt.cancel"),
          }).then(async (result2) => {
            if (result2.isConfirmed) {
              try {
                const updatedCategory = JSON.parse(
                  JSON.stringify(this.myDepartment)
                );

                updatedCategory.subCategories =
                  updatedCategory.subCategories.filter(
                    (sub) => sub._id !== item._id
                  );

                await axios.put(
                  `${process.env.VUE_APP_APP_URL}/category`,
                  updatedCategory
                );

                Swal.fire(
                  this.$t("mgmt.success"),
                  this.$t("jobTypeMgmt.delSubOk"),
                  "success"
                );
                this.fetchMyDepartment();
              } catch (err) {
                Swal.fire(
                  this.$t("mgmt.error"),
                  err.response?.data?.message || this.$t("mgmt.deleteFail"),
                  "error"
                );
              }
            }
          });
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.actions-column {
  white-space: nowrap;
}

.job-type-icon {
  color: #1e40af;
}

.job-icon-select {
  display: flex;
  align-items: center;
  gap: 10px;
}

.job-icon-select svg,
.job-icon-select .c-icon {
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  color: #1e40af;
}
</style>
